import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "@crema/utility/RichTextEditor";
import AppConfirmDialog from "@crema/core/AppConfirmDialog";
import { AppState } from "redux/store";
import {
  getInvestorDeskSections,
  updateInvestorDeskSection,
  getReportTypes,
  getReportYears,
  getFinancialDocumentsByType,
  getFinancialDocumentsByTypeAndYear,
  deleteFinancialDocument,
  getSubsidiariesList,
} from "redux/actions";
import {
  ReportType,
  ReportYear,
  InvestorDeskReport,
} from "types/models/investorDeskMaster/InvestorDeskMasterApp";
import AddFileDialog from "./AddFileDialog";

const QUARTERS = [
  { id: 1, name: "Quarter-1" },
  { id: 2, name: "Quarter-2" },
  { id: 3, name: "Quarter-3" },
  { id: 4, name: "Quarter-4" },
];

const FinancialInformationPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const { sections, reportTypes, reportYears, documents } = useSelector<
    AppState,
    AppState["investorDeskMaster"]
  >(({ investorDeskMaster }) => investorDeskMaster);

  // Form states for Header & Content
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [sectionTitle, setSectionTitle] = useState("Yearly Snapshot");
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [isLoadingSection, setIsLoadingSection] = useState(false);
  const [isSavingSection, setIsSavingSection] = useState(false);

  // Financial Report Types & Filters
  const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedQuarter, setSelectedQuarter] = useState<number | "">("");
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");

  // Search results & table
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Dialog & notifications
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Load initial data
  useEffect(() => {
    setIsLoadingSection(true);
    dispatch(getInvestorDeskSections());
    dispatch(getReportTypes());
    dispatch(getReportYears());
    dispatch(getSubsidiariesList() as any).then((data: any) => {
      if (Array.isArray(data)) setCompanies(data);
    });
  }, [dispatch]);

  // Find Financial Information / Yearly Snapshot section
  useEffect(() => {
    if (sections && sections.length > 0) {
      setIsLoadingSection(false);
      const matched = sections.find((item: InvestorDeskReport) => {
        const t = (item?.title || "").toLowerCase();
        return (
          t.includes("financial information") ||
          t.includes("financial") ||
          t.includes("yearly snapshot")
        );
      });

      if (matched) {
        setSectionId(matched.id);
        setSectionTitle(matched.title || "Yearly Snapshot");
        setHeading(matched.heading || "");
        setContent(matched.content || "");
      }
    }
  }, [sections]);

  // Financial report types: filter for report_id === 3 or matching names
  const financialReportTypes = React.useMemo(() => {
    if (!reportTypes || reportTypes.length === 0) return [];
    const filtered = reportTypes.filter(
      (t: any) =>
        t?.report_id === 3 ||
        ["Results", "Annual Report", "Annual Return", "Financial of Subsidiaries"].includes(
          t.name
        )
    );
    return filtered.length > 0 ? filtered : reportTypes;
  }, [reportTypes]);

  // Auto-select first report type when available
  useEffect(() => {
    if (financialReportTypes.length > 0 && !selectedReportType) {
      setSelectedReportType(financialReportTypes[0]);
    }
  }, [financialReportTypes, selectedReportType]);

  // Auto-select first year when available
  useEffect(() => {
    if (reportYears && reportYears.length > 0 && !selectedYear) {
      setSelectedYear(reportYears[0].year);
    }
  }, [reportYears, selectedYear]);

  // Trigger search when user clicks Search or after upload/delete
  const executeSearch = (typeId?: number, yearVal?: string) => {
    const targetTypeId = typeId || selectedReportType?.id;
    const targetYear = yearVal || selectedYear;

    if (!targetTypeId) {
      setSnackbar({
        open: true,
        message: "Please select a report type",
        severity: "error",
      });
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    const action = targetYear
      ? getFinancialDocumentsByTypeAndYear(targetTypeId, targetYear)
      : getFinancialDocumentsByType(targetTypeId);

    dispatch(action as any);
  };

  // Sync documents from redux store to local filtered state
  useEffect(() => {
    if (documents) {
      let filtered = [...documents];

      if (selectedQuarter) {
        filtered = filtered.filter(
          (doc) => String(doc.quarter) === String(selectedQuarter)
        );
      }

      if (selectedCompanyId) {
        filtered = filtered.filter(
          (doc) => String(doc.subsidiary) === String(selectedCompanyId)
        );
      }

      setFilteredDocuments(filtered);
      setIsSearching(false);
    }
  }, [documents, selectedQuarter, selectedCompanyId]);

  // Handle section content submit
  const handleSectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim() || !content.trim()) {
      setSnackbar({
        open: true,
        message: "Please fill in heading and content",
        severity: "error",
      });
      return;
    }

    if (!sectionId) {
      setSnackbar({
        open: true,
        message: "Financial Information section not found to update",
        severity: "error",
      });
      return;
    }

    setIsSavingSection(true);
    try {
      await dispatch(
        updateInvestorDeskSection(sectionId, {
          title: sectionTitle || "Yearly Snapshot",
          heading,
          content,
          status: "active",
        }) as any
      );
      setSnackbar({
        open: true,
        message: "Financial information updated successfully",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to update financial information",
        severity: "error",
      });
    } finally {
      setIsSavingSection(false);
    }
  };

  const isResultsType = selectedReportType?.name === "Results";
  const isSubsidiariesType =
    selectedReportType?.name === "Financial of Subsidiaries" ||
    selectedReportType?.name === "Subsidiaries";

  const handleReportTypeSelect = (rt: ReportType) => {
    setSelectedReportType(rt);
    setSelectedQuarter("");
    setSelectedCompanyId("");
    setHasSearched(false);
    setFilteredDocuments([]);
  };

  const handleDownload = (file: any) => {
    if (file.file_url) {
      window.open(file.file_url, "_blank");
    } else {
      setSnackbar({
        open: true,
        message: "File URL is not available",
        severity: "error",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await dispatch(deleteFinancialDocument(deleteId) as any);
      setSnackbar({
        open: true,
        message: "Document deleted successfully",
        severity: "success",
      });
      executeSearch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to delete document",
        severity: "error",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const paginatedDocs = filteredDocuments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 50,
              height: 50,
              borderRadius: "12px",
              color: "primary.main",
            }}
          >
            <AssessmentOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.4rem", md: "1.75rem" },
                letterSpacing: "-0.02em",
              }}
            >
             Edit Financial Information
            </Typography>
            <Typography variant="body2">
             Update financial information content
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Main Container Card */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Section 1: Heading & Content Accordion */}
          <form onSubmit={handleSectionSubmit}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                overflow: "hidden",
                mb: 4,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: "#fafafa",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 3,
                  py: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                    Financial Information
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Heading and content for financial information
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 2.5, md: 4 } }}>
                {isLoadingSection ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                      {/* Heading */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            mb: 1,
                            color: "#374151",
                          }}
                        >
                          Heading <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #d1d5db",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#fff",
                            "& .quill": { height: "auto" },
                            "& .ql-container": {
                              minHeight: "80px",
                              fontSize: 14,
                            },
                          }}
                        >
                          <RichTextEditor
                            value={heading}
                            onChange={(val) => setHeading(val)}
                          />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            mb: 1,
                            color: "#374151",
                          }}
                        >
                          Content <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #d1d5db",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#fff",
                            "& .quill": { height: "auto" },
                            "& .ql-container": {
                              minHeight: "160px",
                              fontSize: 14,
                            },
                          }}
                        >
                          <RichTextEditor
                            value={content}
                            onChange={(val) => setContent(val)}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        mt: 4,
                        pt: 3,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        disabled={isSavingSection}
                        sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSavingSection}
                        startIcon={
                          isSavingSection ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{ textTransform: "none", borderRadius: 2, px: 3.5, fontWeight: 600 }}
                      >
                        {isSavingSection ? "Updating..." : "Update"}
                      </Button>
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </form>

          {/* Section 2: Financial Report Types & Document Management */}
          <Box sx={{ mt: 5, pt: 4, borderTop: "2px solid", borderColor: "#f3f4f6" }}>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}>
                Financial Report Types
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Select a report type to view or manage reports
              </Typography>
            </Box>

            {/* Chips List */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
              {financialReportTypes.map((rt) => {
                const isSelected = selectedReportType?.id === rt.id;
                return (
                  <Chip
                    key={rt.id}
                    label={rt.name}
                    clickable
                    onClick={() => handleReportTypeSelect(rt)}
                    icon={isSelected ? <CheckCircleIcon /> : undefined}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    sx={{
                      px: 2,
                      py: 2.2,
                      borderRadius: "50px",
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: 14,
                      borderColor: isSelected ? "primary.main" : "#e5e7eb",
                      bgcolor: isSelected ? "primary.main" : "#f9fafb",
                      color: isSelected ? "#fff" : "#374151",
                      "&:hover": {
                        bgcolor: isSelected ? "primary.dark" : "#f3f4f6",
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Filter Bar */}
            {selectedReportType && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                  bgcolor: "#f9fafb",
                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  mb: 4,
                }}
              >
                {/* Year Dropdown */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Select Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Select Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {reportYears.map((y) => (
                      <MenuItem key={y.id} value={y.year}>
                        {y.year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Quarter Dropdown (Results only) */}
                {isResultsType && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Select Quarter</InputLabel>
                    <Select
                      value={selectedQuarter}
                      label="Select Quarter"
                      onChange={(e) =>
                        setSelectedQuarter(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value="">All Quarters</MenuItem>
                      {QUARTERS.map((q) => (
                        <MenuItem key={q.id} value={q.id}>
                          {q.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Company Dropdown (Subsidiaries only) */}
                {isSubsidiariesType && (
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Select Company</InputLabel>
                    <Select
                      value={selectedCompanyId}
                      label="Select Company"
                      onChange={(e) =>
                        setSelectedCompanyId(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value="">All Companies</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Buttons */}
                <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={
                      isSearching ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={() => executeSearch()}
                    disabled={isSearching || !selectedYear}
                    sx={{ textTransform: "none", borderRadius: 2, px: 2.5, fontWeight: 600 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddDialog(true)}
                    disabled={!selectedYear}
                    sx={{ textTransform: "none", borderRadius: 2, px: 2.5, fontWeight: 600 }}
                  >
                    Add File
                  </Button>
                </Box>
              </Box>
            )}

            {/* Table of Uploaded Files */}
            {(hasSearched || filteredDocuments.length > 0) && (
              <Box ref={resultsRef} sx={{ mt: 4 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 2, color: "text.primary" }}>
                  Uploaded Files
                </Typography>

                {filteredDocuments.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      bgcolor: "#f9fafb",
                      borderRadius: 2,
                      border: "1px dashed #d1d5db",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No files uploaded yet for the selected criteria.
                    </Typography>
                  </Box>
                ) : (
                  <Card
                    sx={{
                      borderRadius: 2.5,
                      border: "1px solid #e5e7eb",
                      boxShadow: "none",
                      overflow: "hidden",
                    }}
                  >
                    <Table>
                      <TableHead sx={{ bgcolor: "#f9fafb" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            Document Title
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            Year
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            File Type
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 600, color: "#4b5563" }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDocs.map((doc) => (
                          <TableRow key={doc.id} hover>
                            <TableCell sx={{ fontWeight: 500, color: "#111827" }}>
                              {doc.document_title || doc.title}
                            </TableCell>
                            <TableCell sx={{ color: "#4b5563" }}>
                              {doc.report_year}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  doc.mime_type
                                    ? doc.mime_type.split("/")[1] || doc.mime_type
                                    : "file"
                                }
                                sx={{
                                  borderRadius: 1,
                                  fontSize: 12,
                                  bgcolor: "#e0f2fe",
                                  color: "#0369a1",
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Tooltip title="Download File">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDownload(doc)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete File">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteId(doc.id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <TablePagination
                      component="div"
                      count={filteredDocuments.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                    />
                  </Card>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Add File Dialog */}
      <AddFileDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: "File(s) uploaded successfully",
            severity: "success",
          });
          executeSearch();
        }}
        investorDeskId={sectionId}
        reportType={selectedReportType}
        reportYear={
          reportYears.find((y) => y.year === selectedYear) || {
            id: 0,
            year: selectedYear,
            report_year: selectedYear,
            status: "active",
          }
        }
        quarter={
          selectedQuarter
            ? QUARTERS.find((q) => q.id === selectedQuarter) || null
            : null
        }
        company={
          selectedCompanyId
            ? companies.find((c) => c.id === selectedCompanyId) || null
            : null
        }
      />

      {/* Delete Confirmation Dialog */}
      <AppConfirmDialog
        open={deleteId != null}
        onDeny={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete File"
        dialogTitle="Delete File"
      />

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialInformationPage;
